import { EditorSelectionType } from "features/mapDrawTools/enums";
import type { Feature, FeatureCollection } from "geojson";
import { ARABLE_TYPE } from "model/commonTypes";
import { Field, GRASS_TYPE } from "model/fieldTypes";
import type { Postcode } from "model/geolocation";
import { create } from "zustand";

export type Location = {
  latitude: number;
  longitude: number;
};

export type { Postcode } from "model/geolocation";

export type TmpFieldDetails = {
  guid: string;
  name: string;
  farm: string;
  geom?: Feature;
  area?: number;
  animalGroup?: string;
  grassType?: GRASS_TYPE;
  arableType?: ARABLE_TYPE;
};

export type MoveAnimalsDialogDetails = {
  open: boolean;
  currentFieldWithAnimals?: Field;
  newFieldWithAnimals?: Field;
  moveToShed?: boolean;
  // Explicit animal group override. Use this when the field is not currently
  // assigned to the animal group the move belongs to (e.g. rotation editor
  // closing an open event for a no-longer-assigned field).
  animalGroupGuid?: string;
};

/**
 * Open the move-animals dialog for a field-to-field or shed-to-field move.
 * Centralises the logic that was previously duplicated in FieldDetails,
 * FieldAnimalGroupActionMenu, FieldsList and AnimalsLocationMarker.
 */
export function openMoveInDialog(
  newField: Field,
  currentFieldWithAnimals: Field | undefined
) {
  useUIStore.getState().openMoveOutEventDialog({
    open: true,
    newFieldWithAnimals: newField,
    currentFieldWithAnimals,
  });
}

export type PushNotificationModalData = {
  fileName: string;
  uploaderName: string;
} | null;

interface UIState {
  // Drawer/Sheet state
  drawerOpen: boolean;
  mobileBottomSheetExpanded: boolean;

  // Modal state
  proposedEventsModalOpen: boolean;
  certificationProgramApplicationModalOpen: boolean;
  fieldCreatedDialogOpen: boolean;
  sbiImportModalOpen: boolean;
  moveAnimalsDialogDetails: MoveAnimalsDialogDetails;
  pushNotificationModal: {
    open: boolean;
    data: PushNotificationModalData;
  };
  onboardingCompletionDialogOpen: boolean;

  // Programme/Assessment state (migrated from questionnaireStartedSlice and quickAssessmentStartedSlice)
  questionnaireStarted: boolean;
  quickAssessmentStarted: boolean;

  // Map UI state
  zoomToGeom: FeatureCollection | null;
  isEditing: boolean;
  editorIsInTentativeMode: boolean;
  editorSelectionType: EditorSelectionType;

  // Form state (temporary features for map editing)
  tmpFieldDetails: TmpFieldDetails | null;
  tmpFeatures: Feature[];
  tentativeFeature: Feature | null;
  tmpDeletedFeatures: string[];
  growthRate: number | null;

  // Geolocation state (from mapSlice)
  postcodeLocation: Location | null;
  navigatorLocation: Location | null;
  tmpPostcodeList: Postcode[];
  postcodeInputError: boolean;
  isDraggingAnimalMarker: boolean;

  // Selection state (migrated from selected*Slice files)
  programmeActionsQuestionnaireFilledOutIds: string[];

  // Drawer/Sheet actions
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  expandMobileBottomSheet: () => void;
  collapseMobileBottomSheet: () => void;

  // Modal actions
  openProposedEventsModal: () => void;
  closeProposedEventsModal: () => void;
  loadCertificationProgramApplicationModalState: () => void;
  closeCertificationProgramApplicationModal: () => void;
  openAfterFieldCreatedDialog: () => void;
  closeAfterFieldCreatedDialog: () => void;
  openSbiImportModal: () => void;
  closeSbiImportModal: () => void;
  openMoveOutEventDialog: (details: MoveAnimalsDialogDetails) => void;
  closeMoveOutEventDialog: () => void;
  openPushNotificationModal: (data: {
    fileName: string;
    uploaderName: string;
  }) => void;
  closePushNotificationModal: () => void;
  openOnboardingCompletionDialog: () => void;
  closeOnboardingCompletionDialog: () => void;

  // Programme/Assessment actions
  setQuestionnaireStarted: () => void;
  setQuestionnaireFinished: () => void;
  setQuickAssessmentStarted: () => void;
  setQuickAssessmentFinished: () => void;

  // Map UI actions
  setZoomToGeom: (geom: FeatureCollection | null) => void;
  clearZoomToGeom: () => void;
  setIsEditing: (editing: boolean) => void;
  toggleIsEditing: () => void;
  setEditorIsInTentativeMode: (tentative: boolean) => void;
  setEditorSelectionType: (type: EditorSelectionType) => void;

  // Form state actions
  setTmpFieldDetails: (details: TmpFieldDetails | null) => void;
  setTmpFeatures: (features: Feature[]) => void;
  setTentativeFeature: (feature: Feature | null) => void;
  deleteTmpFeature: (featureId: string) => void;
  deleteTmpFeatures: () => void;
  setGrowthRate: (rate: number | null) => void;

  // Geolocation actions
  setPostcodeLocation: (location: Location | null) => void;
  setNavigatorLocation: (location: Location | null) => void;
  setTmpPostcodeList: (postcodes: Postcode[]) => void;
  setPostcodeInputError: (error: boolean) => void;
  setIsDraggingAnimalMarker: (dragging: boolean) => void;
  selectPostcode: (postcode: Postcode) => void;

  // Selection actions
  addFilledOutProgramme: (programmeId: string) => void;

  reset: () => void;
}

const initialState = {
  // Drawer/Sheet
  drawerOpen: true,
  mobileBottomSheetExpanded: false,

  // Modals
  proposedEventsModalOpen: true,
  certificationProgramApplicationModalOpen: true,
  fieldCreatedDialogOpen: false,
  sbiImportModalOpen: false,
  moveAnimalsDialogDetails: { open: false } as MoveAnimalsDialogDetails,
  pushNotificationModal: {
    open: false,
    data: null as PushNotificationModalData,
  },
  onboardingCompletionDialogOpen: false,

  // Programme/Assessment state
  questionnaireStarted: false,
  quickAssessmentStarted: false,

  // Map UI
  zoomToGeom: null as FeatureCollection | null,
  isEditing: false,
  editorIsInTentativeMode: false,
  editorSelectionType: EditorSelectionType.NONE,

  // Form state
  tmpFieldDetails: null as TmpFieldDetails | null,
  tmpFeatures: [] as Feature[],
  tentativeFeature: null as Feature | null,
  tmpDeletedFeatures: [] as string[],
  growthRate: null as number | null,

  // Geolocation state
  postcodeLocation: null as Location | null,
  navigatorLocation: null as Location | null,
  tmpPostcodeList: [] as Postcode[],
  postcodeInputError: false,
  isDraggingAnimalMarker: false,

  // Selection state
  programmeActionsQuestionnaireFilledOutIds: [] as string[],
};

export const useUIStore = create<UIState>((set) => ({
  ...initialState,

  // Drawer/Sheet actions
  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false }),
  toggleDrawer: () => set((state) => ({ drawerOpen: !state.drawerOpen })),
  expandMobileBottomSheet: () => set({ mobileBottomSheetExpanded: true }),
  collapseMobileBottomSheet: () => set({ mobileBottomSheetExpanded: false }),

  // Modal actions
  openProposedEventsModal: () => set({ proposedEventsModalOpen: true }),
  closeProposedEventsModal: () => set({ proposedEventsModalOpen: false }),
  loadCertificationProgramApplicationModalState: () => {
    const closed = !!window.localStorage.getItem(
      "certificationProgramApplicationModalClosed"
    );
    set({ certificationProgramApplicationModalOpen: !closed });
  },
  closeCertificationProgramApplicationModal: () => {
    window.localStorage.setItem(
      "certificationProgramApplicationModalClosed",
      "true"
    );
    set({ certificationProgramApplicationModalOpen: false });
  },
  openAfterFieldCreatedDialog: () => set({ fieldCreatedDialogOpen: true }),
  closeAfterFieldCreatedDialog: () => set({ fieldCreatedDialogOpen: false }),
  openSbiImportModal: () => set({ sbiImportModalOpen: true }),
  closeSbiImportModal: () => set({ sbiImportModalOpen: false }),
  openMoveOutEventDialog: (details) =>
    set({ moveAnimalsDialogDetails: details }),
  closeMoveOutEventDialog: () =>
    set({ moveAnimalsDialogDetails: { open: false } }),
  openPushNotificationModal: (data) =>
    set({ pushNotificationModal: { open: true, data } }),
  closePushNotificationModal: () =>
    set({ pushNotificationModal: { open: false, data: null } }),
  openOnboardingCompletionDialog: () =>
    set({ onboardingCompletionDialogOpen: true }),
  closeOnboardingCompletionDialog: () =>
    set({ onboardingCompletionDialogOpen: false }),

  // Programme/Assessment actions
  setQuestionnaireStarted: () => set({ questionnaireStarted: true }),
  setQuestionnaireFinished: () => set({ questionnaireStarted: false }),
  setQuickAssessmentStarted: () => set({ quickAssessmentStarted: true }),
  setQuickAssessmentFinished: () => set({ quickAssessmentStarted: false }),

  // Map UI actions
  setZoomToGeom: (zoomToGeom) => set({ zoomToGeom }),
  clearZoomToGeom: () => set({ zoomToGeom: null }),
  setIsEditing: (isEditing) => set({ isEditing }),
  toggleIsEditing: () => set((state) => ({ isEditing: !state.isEditing })),
  setEditorIsInTentativeMode: (editorIsInTentativeMode) =>
    set({ editorIsInTentativeMode }),
  setEditorSelectionType: (editorSelectionType) => set({ editorSelectionType }),

  // Form state actions
  setTmpFieldDetails: (tmpFieldDetails) => set({ tmpFieldDetails }),
  setTmpFeatures: (tmpFeatures) => set({ tmpFeatures }),
  setTentativeFeature: (tentativeFeature) => set({ tentativeFeature }),
  deleteTmpFeature: (featureId) =>
    set((state) => ({
      tmpDeletedFeatures: [...state.tmpDeletedFeatures, featureId],
    })),
  deleteTmpFeatures: () => set({ tmpFeatures: [], tmpDeletedFeatures: [] }),
  setGrowthRate: (growthRate) => set({ growthRate }),

  // Geolocation actions
  setPostcodeLocation: (postcodeLocation) => set({ postcodeLocation }),
  setNavigatorLocation: (navigatorLocation) => set({ navigatorLocation }),
  setTmpPostcodeList: (tmpPostcodeList) => set({ tmpPostcodeList }),
  setPostcodeInputError: (postcodeInputError) => set({ postcodeInputError }),
  setIsDraggingAnimalMarker: (isDraggingAnimalMarker) =>
    set({ isDraggingAnimalMarker }),
  selectPostcode: (postcode) =>
    set({
      postcodeLocation: {
        latitude: postcode.latitude,
        longitude: postcode.longitude,
      },
    }),

  // Selection actions
  addFilledOutProgramme: (programmeId) =>
    set((state) => ({
      programmeActionsQuestionnaireFilledOutIds: [
        ...state.programmeActionsQuestionnaireFilledOutIds,
        programmeId,
      ],
    })),

  reset: () => set(initialState),
}));

// =============================================================================
// Typed Selectors
// =============================================================================
// Use these instead of inline selectors to get autocomplete and prevent typos.
// Example: const drawerOpen = useUIStore(selectDrawerOpen);

// Drawer/Sheet selectors
export const selectDrawerOpen = (state: UIState) => state.drawerOpen;
export const selectMobileBottomSheetExpanded = (state: UIState) =>
  state.mobileBottomSheetExpanded;

// Modal selectors
export const selectProposedEventsModalOpen = (state: UIState) =>
  state.proposedEventsModalOpen;
export const selectCertificationProgramApplicationModalOpen = (
  state: UIState
) => state.certificationProgramApplicationModalOpen;
export const selectFieldCreatedDialogOpen = (state: UIState) =>
  state.fieldCreatedDialogOpen;
export const selectMoveAnimalsDialogDetails = (state: UIState) =>
  state.moveAnimalsDialogDetails;
export const selectPushNotificationModal = (state: UIState) =>
  state.pushNotificationModal;

// Programme/Assessment selectors
export const selectQuestionnaireStarted = (state: UIState) =>
  state.questionnaireStarted;
export const selectQuickAssessmentStarted = (state: UIState) =>
  state.quickAssessmentStarted;

// Map UI selectors
export const selectZoomToGeom = (state: UIState) => state.zoomToGeom;
export const selectIsEditing = (state: UIState) => state.isEditing;
export const selectEditorIsInTentativeMode = (state: UIState) =>
  state.editorIsInTentativeMode;
export const selectEditorSelectionType = (state: UIState) =>
  state.editorSelectionType;

// Form state selectors
export const selectTmpFieldDetails = (state: UIState) => state.tmpFieldDetails;
export const selectTmpFeatures = (state: UIState) => state.tmpFeatures;
export const selectTentativeFeature = (state: UIState) =>
  state.tentativeFeature;
export const selectTmpDeletedFeatures = (state: UIState) =>
  state.tmpDeletedFeatures;
export const selectGrowthRate = (state: UIState) => state.growthRate;

// Geolocation selectors
export const selectPostcodeLocation = (state: UIState) =>
  state.postcodeLocation;
export const selectNavigatorLocation = (state: UIState) =>
  state.navigatorLocation;
export const selectTmpPostcodeList = (state: UIState) => state.tmpPostcodeList;
export const selectPostcodeInputError = (state: UIState) =>
  state.postcodeInputError;
export const selectIsDraggingAnimalMarker = (state: UIState) =>
  state.isDraggingAnimalMarker;

// Selection selectors
export const selectProgrammeActionsQuestionnaireFilledOutIds = (
  state: UIState
) => state.programmeActionsQuestionnaireFilledOutIds;
